import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Button
} from '@mui/material';
import {
  Preview as PreviewIcon, Close as CloseIcon,
  DesktopWindows as DesktopIcon, Tablet as TabletIcon, PhoneAndroid as MobileIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FormPreview from '../../../components/form-builder/FormPreview';

const PreviewModal = ({ 
  open, 
  onClose, 
  previewDevice, 
  onDeviceChange, 
  formTitle, 
  formDescription, 
  formFields, 
  formSettings, 
  logoUrl, 
  calculationValues 
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          width: '100%',
          m: 2,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #f8fafc, #f1f5f9)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        py: 2,
        px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PreviewIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6" component="span" sx={{ 
            fontWeight: 600,
            color: '#1e293b',
            letterSpacing: '-0.025em'
          }}>
            {t('form_preview')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            bgcolor: '#f1f5f9',
            p: 0.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#e2e8f0'
          }}>
            <Button
              variant={previewDevice === 'desktop' ? 'contained' : 'outlined'}
              startIcon={<DesktopIcon />}
              onClick={() => onDeviceChange('desktop')}
              size="small"
              sx={{ 
                minWidth: 'auto',
                px: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: previewDevice === 'desktop' ? 'white' : '#64748b',
                bgcolor: previewDevice === 'desktop' ? 'primary.main' : 'transparent',
                borderColor: previewDevice === 'desktop' ? 'primary.main' : '#e2e8f0',
                '&:hover': {
                  bgcolor: previewDevice === 'desktop' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                  borderColor: previewDevice === 'desktop' ? 'primary.dark' : 'primary.main',
                  color: previewDevice === 'desktop' ? 'white' : 'primary.main'
                }
              }}
            >
              {t('desktop')}
            </Button>
            <Button
              variant={previewDevice === 'tablet' ? 'contained' : 'outlined'}
              startIcon={<TabletIcon />}
              onClick={() => onDeviceChange('tablet')}
              size="small"
              sx={{ 
                minWidth: 'auto',
                px: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: previewDevice === 'tablet' ? 'white' : '#64748b',
                bgcolor: previewDevice === 'tablet' ? 'primary.main' : 'transparent',
                borderColor: previewDevice === 'tablet' ? 'primary.main' : '#e2e8f0',
                '&:hover': {
                  bgcolor: previewDevice === 'tablet' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                  borderColor: previewDevice === 'tablet' ? 'primary.dark' : 'primary.main',
                  color: previewDevice === 'tablet' ? 'white' : 'primary.main'
                }
              }}
            >
              {t('tablet')}
            </Button>
            <Button
              variant={previewDevice === 'mobile' ? 'contained' : 'outlined'}
              startIcon={<MobileIcon />}
              onClick={() => onDeviceChange('mobile')}
              size="small"
              sx={{ 
                minWidth: 'auto',
                px: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: previewDevice === 'mobile' ? 'white' : '#64748b',
                bgcolor: previewDevice === 'mobile' ? 'primary.main' : 'transparent',
                borderColor: previewDevice === 'mobile' ? 'primary.main' : '#e2e8f0',
                '&:hover': {
                  bgcolor: previewDevice === 'mobile' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                  borderColor: previewDevice === 'mobile' ? 'primary.dark' : 'primary.main',
                  color: previewDevice === 'mobile' ? 'white' : 'primary.main'
                }
              }}
            >
              {t('mobile')}
            </Button>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ 
              bgcolor: '#f1f5f9',
              '&:hover': {
                bgcolor: '#e2e8f0'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ 
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: 'calc(90vh - 120px)',
        overflow: 'auto'
      }}>
        <Box sx={{
          width: previewDevice === 'desktop' ? '100%' :
                 previewDevice === 'tablet' ? '768px' : '375px',
          transition: 'width 0.3s ease',
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.02), 0 10px 15px -3px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <FormPreview
            id="form-preview"
            formTitle={formTitle}
            formDescription={formDescription}
            formFields={formFields}
            settings={formSettings}
            logoUrl={logoUrl}
            showSubmitButton={false}
            calculationValues={calculationValues}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
